import { ref } from 'vue';
import axios from 'axios';
import { stripConfiguratorCatalog } from '@shared/utils/stripConfiguratorCatalog';
// import { useRuntimeConfig } from '#imports';

const createApi = () => {
  // const config = useRuntimeConfig();
  return axios.create({
    baseURL: config.public.apiBaseUrl,
  });
};

class SessionService {
  constructor() {
    this.currentSubmissionId = ref(null);
    this.currentAccessToken = ref(null);
    this.isResumedSession = ref(false);
    this.isLoadingSubmission = ref(false);
  }

  // Check URL for existing submission ID and access token
  detectSubmissionFromUrl(route) {
    const urlSubmissionId = route.query.submission;
    const urlAccessToken = route.query.token;

    if (urlSubmissionId && urlAccessToken) {
      this.currentSubmissionId.value = urlSubmissionId;
      this.currentAccessToken.value = urlAccessToken;
      this.isResumedSession.value = true;
      return { submissionId: urlSubmissionId, accessToken: urlAccessToken };
    }
    return null;
  }

  // Fetch submission data from API using access token
  async fetchSubmission(formId, submissionId, accessToken) {
    try {
      const api = createApi();
      const response = await api.get(`/public/forms/${formId}/submissions/${submissionId}`, {
        params: { token: accessToken },
      });
      return response;
    } catch (error) {
      console.error('Error fetching submission:', error);
      throw error;
    }
  }

  // Save submission (partial or full)
  async saveSubmission(
    formId,
    formData,
    submissionId = null,
    isPartial = true,
    currentPageIndex = null,
    totalSteps = null,
    cpqResult = undefined
  ) {
    try {
      // Use LOCAL Nuxt API for submissions (not external API)
      // The external API is only for fetching with tokens

      // Build payload based on submission type
      const payload = {
        isPartial,
        submissionData: stripConfiguratorCatalog(formData),
        ...(submissionId && { submissionId }),
        ...(cpqResult && { cpqResult }),
      };

      // Add step information for partial submissions
      if (isPartial && currentPageIndex !== null) {
        payload.currentStep = currentPageIndex + 1;
        if (totalSteps !== null) {
          payload.totalSteps = totalSteps;
        }
      }

      // Call LOCAL Nuxt API endpoint (no authentication required)
      const response = await $fetch(`/api/forms/${formId}/submit`, {
        method: 'POST',
        body: payload,
      });

      // Update current submission ID and access token if we got them back
      if (response.submissionId) {
        this.currentSubmissionId.value = response.submissionId;
      }
      if (response.accessToken) {
        this.currentAccessToken.value = response.accessToken;
      }

      return response;
    } catch (error) {
      console.error('Error saving submission:', error);
      throw error;
    }
  }

  async createDraftSubmission(formId) {
    try {
      const response = await $fetch(`/api/forms/${formId}/draft`, {
        method: 'POST',
      });

      if (response.submissionId) {
        this.currentSubmissionId.value = response.submissionId;
      }

      return response;
    } catch (error) {
      console.error('Error creating draft submission:', error);
      throw error;
    }
  }

  // Load existing submission and populate form data
  async loadExistingSubmission(formId, route, router, formData) {
    const urlData = this.detectSubmissionFromUrl(route);
    if (!urlData) return null;

    this.isLoadingSubmission.value = true;

    try {
      const response = await this.fetchSubmission(
        formId,
        urlData.submissionId,
        urlData.accessToken
      );

      if (response.status === 200 && response.data?.data) {
        // Transform the field array into an object with fieldId as keys
        const fieldData = {};
        response.data.data.forEach((field) => {
          if (field.fieldId && field.value !== undefined) {
            fieldData[field.fieldId] = field.value;
          }
        });

        // Merge existing data with form data
        Object.assign(formData.value, fieldData);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to load submission:', error);
      // Clear invalid submission ID from URL
      this.clearSessionFromUrl(route, router);
    } finally {
      this.isLoadingSubmission.value = false;
    }

    return null;
  }

  // Update URL with submission ID and access token
  updateUrlWithSubmission(route, router, submissionId, accessToken) {
    if (submissionId && accessToken) {
      const shouldUpdate =
        submissionId !== route.query.submission || accessToken !== route.query.token;

      if (shouldUpdate) {
        router.replace({
          query: {
            ...route.query,
            submission: submissionId,
            token: accessToken,
          },
        });
      }
    }
  }

  // Clear session from URL
  clearSessionFromUrl(route, router) {
    const query = { ...route.query };
    delete query.submission;
    delete query.token;
    router.replace({ query });
    this.currentSubmissionId.value = null;
    this.currentAccessToken.value = null;
    this.isResumedSession.value = false;
  }

  // Update submission ID and access token from navigation
  updateSubmissionData(submissionId, accessToken) {
    if (submissionId) {
      this.currentSubmissionId.value = submissionId;
    }
    if (accessToken) {
      this.currentAccessToken.value = accessToken;
    }
  }

  // Clear session (on form completion)
  clearSession(route, router) {
    this.clearSessionFromUrl(route, router);
  }

  // Get current state
  getState() {
    return {
      currentSubmissionId: this.currentSubmissionId,
      currentAccessToken: this.currentAccessToken,
      isResumedSession: this.isResumedSession,
      isLoadingSubmission: this.isLoadingSubmission,
    };
  }
}

// Export singleton instance
export const sessionService = new SessionService();
